import React, { useEffect, useRef, useState } from 'react';

import { Table } from '@/components/ui/table';

import { Button } from '@/components/ui/button';
import useHttp from '@/hooks/use-http';
import { EnhancedTableBody } from './async-data-table-body';
import { EnhancedTableHead } from './async-data-table-head';
import { EnhancedTableToolbar } from './async-data-table-toolbar';
import { TablePagination } from './table-pagination';
import { AsyncDataTablePropsType } from './type';

const AsyncDataTable = ({
  dataUrl,
  columns,
  FilterComponent,
  refreshPage = false,
  autoIndex,
  orderByDef = 'created_at',
  orderDef = 'desc',
  btnAction,
  btnText,
  hideFilter,
  hideHeader,
  isDisabledTbLoading,
  defaultFilterProp = [],
  defaultFilterVals = [],
  fetchSize,
  onFilterChange,
  // New header props
  showPageHeader = true,
  pageTitle,
  pageSubtitle,
  headerAction,
  headerActionText,
  headerActionIcon,
  onClickRow,
  onClearFitler,
  highlightedRowId: highlightedRowIdProp,
}: AsyncDataTablePropsType) => {
  const [order, setOrder] = useState<'asc' | 'desc'>(orderDef);
  const [orderBy, setOrderBy] = useState(orderByDef);
  const [selected] = useState<readonly number[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(fetchSize ?? 10);
  const [search, setSearch] = useState('');
  const [propSearch, setPropSearch] = useState(defaultFilterProp);
  const [isFetching, setIsFetching] = useState(false);
  const [highlightedRowIdInternal, setHighlightedRowId] = useState<string | null>(null);

  // Use prop if provided, otherwise use internal state
  const highlightedRowId = highlightedRowIdProp !== undefined ? highlightedRowIdProp : highlightedRowIdInternal;

  // Keep track of previous search values to detect changes
  const prevSearchRef = useRef(search);
  const prevPropSearchRef = useRef(propSearch);
  const lastRequestKeyRef = useRef<string | null>(null);
  const lastRequestTimeRef = useRef<number>(0);

  const { loading: tableLoading, data: requestData, sendRequest } = useHttp();

  const tableData = requestData?.content || requestData;
  const totalFilter = requestData?.totalElements || requestData?.length;


  useEffect(() => {
    if (onFilterChange) {
      onFilterChange([...propSearch]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propSearch]);

  // Fetch data with controlled timing
  const fetchData = async () => {
    if (isFetching) return;

    setIsFetching(true);
    try {
      // Check if search parameters have changed
      const searchChanged = prevSearchRef.current !== search;
      const propSearchChanged =
        JSON.stringify(prevPropSearchRef.current) !== JSON.stringify(propSearch);

      // If search parameters changed, use page 0; otherwise use current page
      const pageToUse = searchChanged || propSearchChanged ? 0 : page;

      // Update refs for next comparison
      prevSearchRef.current = search;
      prevPropSearchRef.current = propSearch;

      // Reset page state if we're using page 0 due to search changes
      if (pageToUse === 0 && page !== 0) {
        setPage(0);
      }

      // Always include defaultFilterVals in searchProp, but deduplicate by propName
      // If a prop exists in both, prefer the one from propSearch (user's current selection)
      const propSearchMap = new Map(propSearch.map((prop: any) => [prop.propName, prop]));

      // Combine: use propSearch values first, then fall back to defaultFilterVals for props not in propSearch
      const combinedSearchProp = [
        ...defaultFilterVals.filter((prop: any) => !propSearchMap.has(prop.propName)),
        ...propSearch
      ];

      // Transform searchProp to match backend API (only propName and value)
      const transformedSearchProp = combinedSearchProp
        .filter((prop) => prop.value && prop.value.toString().trim() !== '')
        .map((prop) => ({
          propName: prop.propName,
          value: prop.value ? prop.value.toString() : null,
        }));

      const postData = {
        filterText: search || null,
        searchProp: transformedSearchProp.length > 0 ? transformedSearchProp : null,
        page: pageToUse,
        size: rowsPerPage,
        sortBy: orderBy,
        ordinal: order,
      };

      const requestKey = JSON.stringify(postData);
      const now = Date.now();
      if (
        lastRequestKeyRef.current === requestKey &&
        now - lastRequestTimeRef.current < 300
      ) {
        setIsFetching(false);
        return;
      }
      lastRequestKeyRef.current = requestKey;
      lastRequestTimeRef.current = now;

      await sendRequest(dataUrl, 'POST', postData);
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    // The debounce has already been applied in the FilterHeader component
    // This effect now focuses on fetching data when dependencies change
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataUrl, rowsPerPage, page, search, orderBy, order, propSearch, refreshPage]);

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: string) => {
    setOrder((preOrder) => (preOrder === 'asc' ? 'desc' : 'asc'));
    setOrderBy(property);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const handleClickRow = (row: any, event?: React.MouseEvent) => {
    if (onClickRow) {
      onClickRow(row, event);
    }
  };

  const filterColumn = columns.filter((e) => e.searchable === true);

  return (
    <div className="w-full space-y-6">
      {/* Optional Page Header */}
      {showPageHeader && (
        <div className="flex flex-col gap-4">
          <div className="space-y-1">
            {pageTitle && (
              <h1 className="text-3xl font-bold text-gray-900 tracking-tight">{pageTitle}</h1>
            )}
            {pageSubtitle && <p className="text-sm text-gray-600 font-medium">{pageSubtitle}</p>}
          </div>
          {headerAction && headerActionText && (
            <Button
              className="gap-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-lg hover:shadow-xl transition-all duration-200 font-medium px-6 py-2.5"
              onClick={headerAction}
            >
              {headerActionIcon}
              <span>{headerActionText}</span>
            </Button>
          )}
        </div>
      )}

      {/* Main Table Container */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {!hideFilter && (
          <EnhancedTableToolbar
            FilterComponent={FilterComponent}
            filterColumn={filterColumn}
            propSearch={propSearch}
            setPropSearch={setPropSearch}
            searchFunction={setSearch}
            searchValue={search}
            btnAction={btnAction}
            btnText={btnText}
            onClearFitler={onClearFitler}
          />
        )}

        <div className="overflow-x-auto w-full">
          <Table aria-labelledby="tableTitle" className="async-data-table w-full">
            {!hideHeader && (
              <EnhancedTableHead
                numSelected={selected.length}
                order={order}
                orderBy={orderBy}
                autoIndex={autoIndex}
                onRequestSort={handleRequestSort}
                rowCount={tableData?.length || 0}
                columns={columns}
              />
            )}
            <EnhancedTableBody
              autoIndex={autoIndex || false}
              loading={tableLoading}
              tableData={tableData}
              columns={columns}
              page={page}
              rowsPerPage={rowsPerPage}
              isDisabledTbLoading={isDisabledTbLoading}
              onClickRow={handleClickRow}
              highlightedRowId={highlightedRowId}
            />
          </Table>
        </div>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50, 100, 1000, 5000]}
          count={totalFilter ?? 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </div>
    </div>
  );
};

export default AsyncDataTable;
